import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Sparkles, ShieldCheck, TrendingUp, Calculator, DollarSign, MessageSquare,
  Zap, Video, MapPin, Users, BarChart3, Mail,
} from 'lucide-react';
import api from '@/lib/api';
import logger from '@/lib/logger';

const ICON_MAP = {
  ShieldCheck, TrendingUp, Calculator, DollarSign, MessageSquare,
  Zap, Video, MapPin, Users, BarChart3, Mail, Sparkles,
};

export function WhatsNewModal() {
  const [open, setOpen] = useState(false);
  const [releases, setReleases] = useState([]);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    api.getWhatsNew()
      .then((r) => {
        const rels = r.data?.releases || [];
        if (rels.length > 0) {
          setReleases(rels);
          setOpen(true);
        }
      })
      .catch((e) => logger.error(e));
  }, []);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await api.dismissWhatsNew();
    } catch (e) {
      logger.error(e);
    } finally {
      setDismissing(false);
      setOpen(false);
    }
  };

  if (!open || releases.length === 0) return null;
  // Show the newest release only; older releases batch into a "+N earlier" pill.
  const headline = releases[0];
  const earlierCount = releases.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDismiss(); }}>
      <DialogContent
        className="max-w-lg rounded-sm p-0 overflow-hidden"
        data-testid="whats-new-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0047AB] to-[#0066D6] px-6 py-5 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-wider opacity-80">
              What's New &middot; {headline.date}
            </span>
          </div>
          <DialogHeader className="text-left p-0">
            <DialogTitle
              className="text-white text-xl font-bold font-heading leading-tight"
              data-testid="whats-new-title"
            >
              {headline.title}
            </DialogTitle>
          </DialogHeader>
          {earlierCount > 0 && (
            <p className="text-[11px] opacity-80 mt-2">
              + {earlierCount} earlier release{earlierCount === 1 ? '' : 's'} you haven&apos;t seen.
            </p>
          )}
        </div>

        {/* Items */}
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto" data-testid="whats-new-items">
          <div className="space-y-4">
            {headline.items.map((item, idx) => {
              const Icon = ICON_MAP[item.icon] || Sparkles;
              return (
                <div key={idx} className="flex gap-3" data-testid={`whats-new-item-${idx}`}>
                  <div className="flex-shrink-0 w-8 h-8 rounded-sm bg-[#0047AB]/10 text-[#0047AB] flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="border-t border-slate-200 bg-slate-50 px-6 py-3 flex sm:justify-between gap-2">
          <a
            href="/help"
            className="text-[11px] text-slate-500 hover:text-slate-900 self-center"
            data-testid="whats-new-manual-link"
          >
            Read the full manual &rarr;
          </a>
          <Button
            onClick={handleDismiss}
            disabled={dismissing}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            data-testid="whats-new-dismiss-btn"
          >
            {dismissing ? 'Saving...' : 'Got it'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
